import { alpha, Card, CardContent, CircularProgress, Grid, Paper, Typography, useTheme } from "@mui/material";
import {
  CalendarToday as CalendarIcon,
  FormatListNumbered as OrderIcon,
} from '@mui/icons-material';
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import WSTextField, { TextType } from "../../../Components/TextBox/WSTextBox";
import WSButton from "../../../Components/Button/WSButton";
import { callAPI, Method } from "../../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import { useNavigate, useParams } from "react-router-dom";
import { Header } from "../../../Components/Header/Header";
import WSHiddenField from "../../../Components/HiddenField/WSHiddenField";
import WSDateField from "../../../Components/DateField/WSDateField";
import dayjs from "dayjs";
import BannerPreview from "./BannerImagePreview";
import config from '../../../Config/config.json'

export const BannerImage: React.FC<any> = React.memo((props) => {
  const { control, register, handleSubmit, setValue, resetField } = useForm();
  const { showMessage } = useSnackbar();
  const params = useParams();
  const id = params?.id;
  const theme = useTheme();
  const navigate = useNavigate();

  const [subitting, setSubmitting] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true)
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [defaultImg, setDefaultImg] = useState<any | null>(null);
  const [startDate, setStartDate] = useState<string>()

  useEffect(() => {
    /**
     * Edit Function
     * @param id
     *
     */
    async function getBannerById(id: any) {
      try {
        setLoading(true);
        const response: any = await callAPI(`banners/getBy`, Method.GET, {}, id);

        if (response && response?.data) {
          setValue("name", response.data?.name);
          setValue("startDate", dayjs(response.data?.startDate).format('DD/MM/YYYY'));
          setValue("displayOrder", response.data?.displayOrder);
          setValue("endDate", dayjs(response.data?.endDate).format('DD/MM/YYYY'));
          setDefaultImg(response.data?.imageUrl)
          setValue("id", response.data?._id);
        }
      } catch (error) {
        console.error("failed to retrieve data", error);
      } finally {
        setLoading(false);
      }
    }
    if (id) {
      getBannerById(id);
    }
  }, [id, setValue]);

  /**
   * Method for Add the data
   * @param data
   */
  const handleData = async (data: any) => {
    try {
      setSubmitting(true);
      // Extract image name from the uploaded file.
      let imageName = "";
      if (data.imageUrl && data.imageUrl[0]) {
        const file = data.imageUrl[0]; // Assuming `imageUrl` contains an array of files.
        imageName = file.name; // Get the file name from the uploaded image.
      }

      const startDate = dayjs(data.startDate, "DD/MM/YYYY").format('YYYY-MM-DDTHH:mm:ss.SSS')
      const endDate = dayjs(data.endDate, "DD/MM/YYYY").format('YYYY-MM-DDTHH:mm:ss.SSS')
      const formData = new FormData()
      formData.append("name", data.name);
      formData.append("file", selectedFile!);
      formData.append("imgUrl", data.imgUrl); // Add title
      formData.append("displayOrder", data.displayOrder); // Add display
      formData.append("startDate", startDate);
      formData.append("endDate", endDate);

      if (id) {
        formData.append("id", id); // Add display
        const response: any = await callAPI(`banners/update/${id}`, Method.PUTFORMDATA, null, null, formData);
        if (response) {
          showMessage(response?.message, response.success === true ? "success" : "error");
          response.success === true && navigate("/banner/list");
        }
      } else {
        const response: any = await callAPI("banners/create", Method.POSTFORMDATA, null, null, formData);
        if (response) {
          showMessage(response?.message, response.success === true ? "success" : "error");
          response.success === true && navigate("/banner/list");
        }
      }
    } catch (e: any) {
      showMessage(e?.response?.data?.message || "something went wrong",'error');
    }finally{
      setSubmitting(false);
    }
  };

  const handleUploadFile = (file: File) => {
    setSelectedFile(file);
    setDefaultImg(null);

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };

  const handleRemove = () => {
    resetField("imageUrl");
    setSelectedFile(null);
    setPreviewUrl(null);
    setDefaultImg(null);
  };

  const handleStartDateChange = (evt: any) => {
    setStartDate(evt)
  }
  const handleCancelClick = () => {
    navigate('/banner/list')
  }

  const imageUrl = id && defaultImg ? `${config?.imgUrl}${defaultImg}` : previewUrl;

  {
    loading && (
      <div style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(255,255,255,0.7)',
        zIndex: 9999
      }}>
        <CircularProgress />
      </div>
    )
  }


  return (
    <>
      <Header
        type="simple"
        isDivider={true}
        title={`${id ? 'Edit' : 'Create'} Banner`}
        url="/banner/list"
        id={'bannerimageImg'}
        isBackButton={true}
      />

      <form onSubmit={handleSubmit(handleData)}>
        <Card
          sx={{
            padding: 4,
            backgroundColor: "#fff",
            borderRadius: 3,
            marginTop: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
            background: 'linear-gradient(to bottom, #ffffff, #fafafa)'
          }}
        >
          <CardContent>
            <Typography
              variant="h5"
              sx={{
                mb: 4,

                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                paddingBottom: 1,
                borderBottom: `2px solid ${alpha(theme.palette.secondary.main, 0.2)}`
              }}
            >
              Banner Details
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.secondary.light, 0.05),
                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                    height: '100%'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarIcon fontSize="small" /> Date Settings
                  </Typography>

                  <WSTextField
                    label={"Banner Name"}
                    {...register("name")}
                    id="name"
                    name="name"
                    control={control}
                    rules={{ required: true }}

                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: '#fff',
                        marginBottom: 2,
                      }
                    }}
                  />

                  <WSDateField
                    id="startDate"
                    name="startDate"
                    label={"Start Date"}
                    dateFormat="DD/MM/YYYY"
                    denyFuturOrPast="past"
                    onChange={handleStartDateChange}
                    control={control}
                    defaultValue={null}
                    rules={{ required: true }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: '#fff',

                      }
                    }}
                  />

                  <WSDateField
                    id="endDate"
                    name="endDate"
                    label={"End Date"}
                    dateFormat="DD/MM/YYYY"
                    denyFuturOrPast="custom"
                    control={control}
                    defaultValue={startDate}
                    setValue={setValue}
                    rules={{ required: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: '#fff',

                      }
                    }}
                  />

                  <Typography variant="subtitle2" sx={{ mb: 2, mt: 3, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <OrderIcon fontSize="small" /> Display Settings
                  </Typography>

                  <WSTextField
                    label={"Display Order"}
                    {...register("displayOrder")}
                    id="displayOrder"
                    name="displayOrder"
                    type={TextType.Number}
                    control={control}
                    rules={{ required: true }}

                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: '#fff',

                      }
                    }}
                  />

                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <BannerPreview
                  imageUrl={imageUrl}
                  onUpload={handleUploadFile}
                  onRemove={handleRemove}
                  acceptFileTypes=".jpg,.jpeg,.png"
                  maxFileSizeMB={2}
                  height={160}
                />

              </Grid>

              <Grid item xs={12} sm={6} style={{ display: "none" }}>
                <WSHiddenField
                  name="id"
                  control={control}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sx={{
                  marginTop: "3rem",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                }}
              >
                <WSButton
                  id="submit"
                  label={"Save"}
                  type="submit"
                  size="small"
                  loading={subitting}
                />

                <WSButton
                  id="cancel"
                  label={"Cancel"}
                  type="button"
                  size="small"
                  className="ws-cancel"
                  onClick={handleCancelClick}
                  loading={subitting}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </form>
    </>
  );
});
